import React, { useState, useMemo } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../components/ui/command';
import { Button } from '../components/ui/button';
import { Check, ChevronsUpDown, MapPin } from 'lucide-react';
import { cn } from '../lib/utils';
import { getCountiesForState, getAllCounties } from '../lib/us-counties';

export function CountyAutocomplete({ value, onChange, stateAbbr, disabled, className }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const options = useMemo(() => {
    if (stateAbbr) return getCountiesForState(stateAbbr);
    return getAllCounties().map(c => `${c.county}, ${c.state}`);
  }, [stateAbbr]);

  const filtered = useMemo(() => {
    if (!search) return options.slice(0, 50);
    const q = search.toLowerCase();
    return options.filter(o => o.toLowerCase().includes(q)).slice(0, 50);
  }, [options, search]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between font-body text-sm font-normal h-9",
            !value && "text-muted-foreground",
            className
          )}
          data-testid="county-autocomplete-trigger"
        >
          <span className="truncate flex items-center gap-1.5">
            <MapPin className="w-3 h-3 flex-shrink-0" />
            {value || "Select county..."}
          </span>
          <ChevronsUpDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search county..."
            value={search}
            onValueChange={setSearch}
            data-testid="county-search-input"
          />
          <CommandList>
            <CommandEmpty>
              <div className="py-2 px-3 text-sm">
                <p className="text-muted-foreground mb-2">No county found.</p>
                {search && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full text-primary"
                    onClick={() => { onChange(search); setOpen(false); setSearch(''); }}
                    data-testid="county-custom-entry"
                  >
                    Use "{search}" as county
                  </Button>
                )}
              </div>
            </CommandEmpty>
            <CommandGroup className="max-h-[200px] overflow-y-auto">
              {filtered.map((county) => (
                <CommandItem
                  key={county}
                  value={county}
                  onSelect={() => { onChange(county); setOpen(false); setSearch(''); }}
                  data-testid={`county-option-${county.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  <Check className={cn("mr-2 h-3 w-3", value === county ? "opacity-100" : "opacity-0")} />
                  {county}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
